import React from "react";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { useQueryClient } from "@tanstack/react-query";
import {
    GRID_ACTION_TYPES,
    GridActionType,
    Module,
} from "../../../assets/types/common";
import { isGridActionType } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { IActionResponse, IActionResult } from "../../../services/proxies";
import { ViewProps } from "../props";
import { LookupTypeForm as EditComponent } from "./lookupTypeForm";
import { LookupTypePage } from "./lookupTypePage";
import { LookupTypeService } from "./lookupTypeService";
import { LookupTypeView as LookupTypeViewComponent } from "./lookupTypeView";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

interface LookupTypeProps<T> {
    moduleType: Module;
}

export const LookupTypeContainer = <T extends ViewProps.CategoryTypeDto>(
    props: React.PropsWithoutRef<LookupTypeProps<T>>
) => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();
    const [data, setData] = React.useState<T>();
    const [drawerType, setDrawerType] = React.useState<GridActionType>(
        GRID_ACTION_TYPES.HIDDEN
    );
    const service: LookupTypeService<T> = new LookupTypeService<T>(
        props.moduleType
    );

    const lookupTypeQuery = service.getLookupTypeQuery(data?.id ?? undefined);
    const { setSearchAction, clearSearchAction } = useActionFromQuery(OnCustomEvent);
    function EditRecord(data: any) {
        setData(data);
        setDrawerType(GRID_ACTION_TYPES.EDIT);
        setSearchAction(GRID_ACTION_TYPES.EDIT, data?.id);
    }

    function OnCustomEvent(actionType: string, dataId: string) {
        if (isGridActionType(actionType)) {
            setDrawerType(actionType);
            setData({ id: dataId } as T);
            setSearchAction(actionType, dataId);
        }
    }

    const { status, mutateAsync: mutate } = service.save();
    const { status: deactivatestatus, mutate: deactivateMutate } =
        service.deActivate();

    const handleOnSubmit = async (values: T) => {
        const isEdit = data && data.id && data.id !== Constants.emptyGuid;
        values.id = isEdit ? data.id : Constants.emptyGuid;
        return await mutate(values, { onSuccess: (response: IActionResult<T>) => handleResponse(response, "Lookup Type Saved") });
    };

    const closeDrawer = () => {
        setDrawerType(GRID_ACTION_TYPES.HIDDEN);
        clearSearchAction();
    }

    const handleDelete = () => deactivateMutate(data?.id ?? Constants.emptyGuid, { onSuccess: (response: IActionResponse) => handleResponse(response, "Lookup Type Deleted") });

    const renderDrawerContent = () => {
        switch (drawerType) {
            case GRID_ACTION_TYPES.NEW: {
                return (
                    <EditComponent<T>
                        show={true}
                        onHide={closeDrawer}
                        onSave={(data) => handleOnSubmit(data)}
                        isLoading={false}
                    />
                );
            }
            case GRID_ACTION_TYPES.EDIT:
                return (
                    <EditComponent<T>
                        data={lookupTypeQuery?.data}
                        show={!!lookupTypeQuery?.data}
                        onHide={closeDrawer}
                        onSave={(data) => handleOnSubmit(data)}
                        isLoading={lookupTypeQuery.isLoading}
                    />
                );
            case GRID_ACTION_TYPES.VIEW:
            case GRID_ACTION_TYPES.DOUBLE_CLICK:
                return (
                    <LookupTypeViewComponent<T>
                        data={lookupTypeQuery?.data}
                        show={!!lookupTypeQuery?.data}
                        onHide={closeDrawer}
                        onDelete={handleDelete}
                        onEdit={() => EditRecord(lookupTypeQuery.data)}
                        isLoading={lookupTypeQuery.isLoading}
                    />
                );
        }
    };

    function handleResponse(response: IActionResponse, snackbarMessage: string) {
        if (response) {
            if (response.isSuccess) {
                showSnackbar(snackbarMessage, MessageSeverity.Success);
                lookupTypeQuery.refetch();
                queryClient.invalidateQueries({ queryKey: [ViewProps.LookupTypeQueryKeys[ViewProps.LookupTypeQueryTypes.Grid]] });
                closeDrawer();
            } else {
                if (response.errorMessages) {
                    const errorMsg: string | null = apiErrorMessage(response.errorMessages);
                    snackbarMessage = errorMsg ?? "Error";
                }
                showSnackbar(snackbarMessage, MessageSeverity.Error);
            }
        }
        else {
            showSnackbar("Error, unable to process your request please try again later.", MessageSeverity.Error);
        }
    }

    return (
        <>
            <LookupTypePage<T>
                onCustomEvent={OnCustomEvent}
                moduleType={props.moduleType}
            />
            {renderDrawerContent()}
        </>
    );
};
